// Periodo di validità di un viaggio giornaliero: `start_date` / `end_date`.
//
// I `giorni` dicono in quali giorni della settimana il viaggio si ripete, ma
// non da quando a quando: un trasporto scolastico non vale ad agosto, e uno
// attivato a metà settembre non deve comparire nelle settimane precedenti. I
// fogli operativi lo scrivono a mano nelle note ("inizia dal 2.09", "riprende
// il 14.09"); qui diventa un campo.
//
// I due estremi sono INCLUSIVI e si confrontano per giorno, non per istante: la
// validità è una data di calendario, e un confronto al millisecondo farebbe
// dipendere il risultato dall'ora e dal fuso.
import dayjs from 'dayjs';

import i18n from '@/i18n';
import { toMomentStrict } from '@modules/utils/dates';

/** Formato di persistenza lato UI: data pura, senza ora né fuso. */
export const VALIDITY_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Lettura di un estremo, in QUALSIASI forma il campo arrivi.
 *
 * Non basta `dayjs(value)`: il backend persiste queste date come **Timestamp
 * Firestore**, che il listener serializza in `{ seconds, nanoseconds }`. Col
 * plugin `objectSupport` attivo, `dayjs({ seconds })` è *valido* e interpreta
 * `seconds` come offset da adesso — un timestamp del 2026 diventa il 2083, e
 * ogni giorno risulta "prima dell'inizio validità". `toMomentStrict` riconosce
 * Timestamp, Date, dayjs, stringhe e numeri, e torna null su tutto il resto.
 */
const toDay = (value) => toMomentStrict(value);

/**
 * True se il viaggio è valido nella data indicata.
 *
 * Un viaggio **senza** estremi è valido sempre: è il caso di tutti i pianificati
 * creati prima di questo campo, che non devono sparire da nessuna vista.
 *
 * @param {{start_date?: string, end_date?: string}} trip
 * @param {import('dayjs').Dayjs|Date|string} date
 */
export function isWithinValidity(trip, date) {
  const day = toDay(date);
  if (!day) return true;

  const from = toDay(trip?.start_date);
  if (from && day.isBefore(from, 'day')) return false;

  const to = toDay(trip?.end_date);
  if (to && day.isAfter(to, 'day')) return false;

  return true;
}

/** True se il viaggio ha un periodo di validità dichiarato. */
export const hasValidityRange = (trip) => !!(trip?.start_date || trip?.end_date);

/**
 * Errore di coerenza del periodo, o `null`. Non valida che le date esistano:
 * entrambe sono facoltative.
 */
export function validityError(startDate, endDate) {
  const from = toDay(startDate);
  const to = toDay(endDate);
  if (startDate && !from) return 'start_invalid';
  if (endDate && !to) return 'end_invalid';
  if (from && to && to.isBefore(from, 'day')) return 'end_before_start';
  return null;
}

/**
 * Il pianificato è "in servizio" in questa data — a prescindere dalla cadenza
 * settimanale.
 *
 * Tre campi diversi decidono se un pianificato esiste: `deleted`, `attivo` e il
 * periodo di validità. I primi due sono booleani **senza data**: dicono *se*,
 * mai *da quando* — ed è esattamente ciò che rompe una rendicontazione
 * retroattiva. Un giro chiuso il 30/08 non è "mai esistito": è vero fino al
 * 31/08 e falso dopo.
 *
 * Da qui la regola:
 *
 * - chi **dichiara un periodo** è governato dalle sue date, anche se
 *   `deleted`/`attivo:false` → pianificato **archiviato**, storia vera e chiusa;
 * - chi **non** lo dichiara mantiene la semantica booleana storica
 *   (`deleted` o `attivo:false` → non esiste in nessun giorno).
 *
 * Così il backfill che chiude i vecchi giri si limita ad aggiungere le date,
 * senza toccare `deleted`/`attivo`: riattivarli farebbe ricominciare al cron
 * backend a generare ordini reali da giri non più in servizio.
 */
export function isInService(trip, date) {
  if (!trip) return false;
  if (!isWithinValidity(trip, date)) return false;
  if (hasValidityRange(trip)) return true;
  return !trip.deleted && trip.attivo !== false;
}

/**
 * Giorno di riferimento dei predicati di validità: la data passata normalizzata
 * a mezzanotte, oppure **oggi** se non ne arriva nessuna.
 *
 * Serve a due cose. La prima è la coerenza: `isInService` e `occursOn`
 * confrontano per giorno, quindi un `dayjs()` creato di volta in volta dentro un
 * `filter` non aggiunge precisione — aggiunge solo il rischio che due chiamate
 * della stessa operazione cadano ai due lati della mezzanotte e producano liste
 * incoerenti (il conteggio dice 3, la settimana ne mostra 2). Una sola
 * istanziazione per operazione, passata a tutte le chiamate, toglie il caso.
 *
 * La seconda è la testabilità: chi chiama espone il giorno come parametro
 * facoltativo, così un test può fissarlo invece di dipendere dall'orologio.
 *
 * @param {import('dayjs').Dayjs|Date|string|null} [date]
 * @returns {import('dayjs').Dayjs}
 */
export function referenceDay(date) {
  return (toDay(date) ?? dayjs()).startOf('day');
}

/**
 * Giorno della settimana di un pianificato, convenzione `dayjs().day()`
 * (0 = domenica … 6 = sabato) — la stessa che scrive l'import.
 *
 * NB: parte del codice storico usa `isoWeekday()` (1-7) sugli stessi dati, e i
 * due schemi **divergono la domenica**. Passando tutti da qui la domenica smette
 * di essere un caso speciale.
 */
const occursOnWeekday = (trip, day) =>
  normalizeWeekdays(trip.giorni, trip.giorno).includes(day.day());

/**
 * Normalizes a daily trip's weekdays to the `dayjs().day()` convention
 * (0 = Sunday … 6 = Saturday): numbers only, deduplicated, sorted.
 *
 * Tolerates the legacy form's Sunday = 7 (`isoWeekday`), which never matched
 * `.day()` and silently dropped every Sunday occurrence. Falls back to the
 * singular `giorno` when `giorni` is empty. Twin of the backend
 * `functions/utils/dailyTripValidity.js`.
 *
 * @param {Array<number|string>|null|undefined} giorni
 * @param {number|string|null} [giorno]
 * @returns {number[]}
 */
export function normalizeWeekdays(giorni, giorno = null) {
  const raw =
    Array.isArray(giorni) && giorni.length > 0
      ? giorni
      : giorno != null && giorno !== ''
        ? [giorno]
        : [];
  const days = raw
    .filter((d) => d != null && d !== '')
    .map(Number)
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 7)
    .map((d) => d % 7);
  return [...new Set(days)].sort((a, b) => a - b);
}

/**
 * Il pianificato produce un'occorrenza in questa data? Cadenza settimanale
 * (`giorni`/`giorno`) **e** servizio attivo in quel giorno (`isInService`).
 *
 * È il predicato da usare in ogni espansione giorno-per-giorno: report km,
 * export Excel, generazione assenze, board.
 */
export function occursOn(trip, date) {
  if (!trip) return false;
  const day = toDay(date);
  if (!day) return false;
  if (!occursOnWeekday(trip, day)) return false;
  return isInService(trip, day);
}

/** Stati possibili di un pianificato rispetto a un giorno di riferimento. */
export const VALIDITY_STATES = {
  ARCHIVED: 'archived',
  SCHEDULED: 'scheduled',
  EXPIRED: 'expired',
  ACTIVE: 'active',
};

/**
 * Stato rispetto alle sole **date**: dentro il periodo è valido, prima è
 * programmato, dopo è scaduto. Non guarda `deleted`/`attivo`.
 *
 * È l'asse su cui ragiona l'operatore quando chiede "questo giro vale adesso?",
 * e va tenuto separato dallo stato deciso a mano (attivo/disattivato): sono due
 * domande diverse, e confonderle significa che filtrare per una nasconde l'altra.
 */
export function validityDateState(trip, day) {
  const ref = toDay(day) ?? dayjs();
  if (!trip) return VALIDITY_STATES.EXPIRED;

  const from = toDay(trip.start_date);
  if (from && ref.isBefore(from, 'day')) return VALIDITY_STATES.SCHEDULED;

  const to = toDay(trip.end_date);
  if (to && ref.isAfter(to, 'day')) return VALIDITY_STATES.EXPIRED;

  return VALIDITY_STATES.ACTIVE;
}

/**
 * Stato mostrato in UI. Come `validityDateState`, ma un giro cancellato o
 * disattivato si etichetta `archived`: sulla riga conta di più sapere che è
 * stato chiuso da qualcuno che non il punto del calendario in cui cade.
 */
export function validityState(trip, day) {
  if (!trip) return VALIDITY_STATES.EXPIRED;
  if (trip.deleted || trip.attivo === false) return VALIDITY_STATES.ARCHIVED;
  return validityDateState(trip, day);
}

/** Modalità del filtro validità, condivise da tutte le liste di pianificati. */
export const VALIDITY_FILTERS = {
  ALL: 'all',
  OPEN: 'open',
  CURRENT: 'current',
  SCHEDULED: 'scheduled',
  EXPIRED: 'expired',
  ARCHIVED: 'archived',
};

/**
 * Opzioni del filtro, costruite **alla chiamata**.
 *
 * Non è un array costante perché le label passano da i18n: valutandole a
 * import-time resterebbero nella lingua attiva al primo import, e un cambio
 * lingua non le aggiornerebbe più.
 */
export function getValidityFilterOptions() {
  return [
    {
      value: VALIDITY_FILTERS.OPEN,
      label: i18n.t('validi_e_futuri', { defaultValue: 'Validi e futuri' }),
    },
    {
      value: VALIDITY_FILTERS.CURRENT,
      label: i18n.t('validi_oggi', { defaultValue: 'Validi oggi' }),
    },
    {
      value: VALIDITY_FILTERS.SCHEDULED,
      label: i18n.t('programmati', { defaultValue: 'Programmati' }),
    },
    { value: VALIDITY_FILTERS.EXPIRED, label: i18n.t('scaduti', { defaultValue: 'Scaduti' }) },
    {
      value: VALIDITY_FILTERS.ARCHIVED,
      label: i18n.t('archiviati', { defaultValue: 'Archiviati' }),
    },
    { value: VALIDITY_FILTERS.ALL, label: i18n.t('tutti', { defaultValue: 'Tutti' }) },
  ];
}

/**
 * Predicato del filtro lista.
 *
 * I tre modi di periodo guardano **solo le date**, `archived` solo i booleani:
 * sono due assi indipendenti e si sovrappongono di proposito. Un giro
 * disattivato ma dentro il suo periodo compare sia in "Validi oggi" sia in
 * "Archiviati" — se invece l'archivio vincesse, il default "Validi oggi"
 * nasconderebbe di colpo tutti i disattivati, che è la lista `attivo` a dover
 * governare.
 *
 * `mode` sconosciuto o assente non filtra nulla: una modalità non riconosciuta
 * deve mostrare tutto, non svuotare la tabella.
 */
export function matchesValidityFilter(trip, mode, day) {
  if (!mode || mode === VALIDITY_FILTERS.ALL) return true;
  if (mode === VALIDITY_FILTERS.ARCHIVED) return !!(trip?.deleted || trip?.attivo === false);

  const state = validityDateState(trip, day);
  switch (mode) {
    // "Validi e futuri": tutto ciò che non è ancora storia chiusa. È il default
    // delle liste — un giro che parte lunedì prossimo è una cosa che l'operatore
    // deve vedere *adesso*, non quando comincia.
    case VALIDITY_FILTERS.OPEN:
      return state !== VALIDITY_STATES.EXPIRED;
    case VALIDITY_FILTERS.CURRENT:
      return state === VALIDITY_STATES.ACTIVE;
    case VALIDITY_FILTERS.SCHEDULED:
      return state === VALIDITY_STATES.SCHEDULED;
    case VALIDITY_FILTERS.EXPIRED:
      return state === VALIDITY_STATES.EXPIRED;
    default:
      return true;
  }
}

/**
 * Come `matchesValidityFilter`, ma nasconde i **cancellati** finché non si
 * chiede esplicitamente di vederli (`archiviati` o `tutti`).
 *
 * Serve solo dove la lista arriva grezza da Firestore — le due schede di
 * dettaglio (`getDailyTripsByUserId`, `getDailyTripsByCompany`): la lista
 * globale i `deleted` non li riceve nemmeno, li scarta il fetch.
 *
 * `deleted` e `attivo:false` restano trattati in modo diverso di proposito: il
 * primo è una rimozione, il secondo una pausa. Nascondere anche i disattivati
 * renderebbe di nuovo irraggiungibili le azioni che servono a riattivarli.
 */
export function isVisibleUnderFilter(trip, mode, day) {
  const asked = mode === VALIDITY_FILTERS.ARCHIVED || mode === VALIDITY_FILTERS.ALL;
  if (trip?.deleted && !asked) return false;
  return matchesValidityFilter(trip, mode, day);
}

/** Etichetta compatta del periodo, per le celle di tabella. */
export function validityLabel(trip) {
  const from = toDay(trip?.start_date);
  const to = toDay(trip?.end_date);
  if (!from && !to) return '';
  if (from && to) return `${from.format('DD/MM/YY')} → ${to.format('DD/MM/YY')}`;
  if (from) return `dal ${from.format('DD/MM/YY')}`;
  return `fino al ${to.format('DD/MM/YY')}`;
}
